import * as React from 'react';
import {
  MutableRefObject,
  ReactElement,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import GroupListTable from 'components/groups/GroupListTable';
import { useCLOSETGroupsQuery } from 'hooks/groups/useCLOSETGroupsQuery';
import { GetCLOSETGroupsParams } from 'types/api/groups/closet';
import { CLOSETGroupsInitialSearchParams } from '../../hooks/groups/useCLOSETGroupsInitialSearchParams';
import { getClosetGroups } from 'api/groups/closet';
import { groupLIstExcelColumns } from 'components/groups/GroupListColumns';

export interface GroupsRef {
  fetchGroupList: (
    params: Omit<GetCLOSETGroupsParams, 'pageSize' | 'pageNo'>,
    pageNo: number,
  ) => Promise<void>;
}

interface Props {
  groupsRef?: MutableRefObject<GroupsRef | null>;
  initialSearchParams: CLOSETGroupsInitialSearchParams;
}

const GroupList = (props: Props): ReactElement => {
  const { isLoading, data, params, handleGroupList } = useCLOSETGroupsQuery({
    initialSearchParams: props.initialSearchParams,
  });
  const { pageInfo, list } = data;
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [excelData, setExcelData] = useState<any[]>([{ status: 'NO DATA' }]);
  const ref = useRef<HTMLButtonElement>(null);

  useImperativeHandle(props.groupsRef, () => ({ fetchGroupList: handleGroupList }), [
    handleGroupList,
  ]);

  const currentPage = pageInfo.currentPage;
  const totalPages = pageInfo.totalPages;

  const onChangePage = useCallback(
    (pageIndex: number) => handleGroupList(params, pageIndex),
    [params],
  );

  const onClickExport = async (): Promise<void> => {
    if (!ref.current || exportLoading || isLoading) return;
    try {
      setExportLoading(true);
      const data = await getClosetGroups({
        ...params,
        pageSize: 999999,
        pageNo: 1,
      });
      setExcelData(groupLIstExcelColumns(data));
    } catch (error) {
      console.error('Error exporting group list:', error);
      window.alert('Failed to export group list. Please try again later.');
    } finally {
      setExportLoading(false);
      ref.current.click();
    }
  };

  return (
    <GroupListTable
      ref={ref}
      data={list}
      totalPages={totalPages}
      onChangePage={onChangePage}
      currentPage={currentPage}
      isLoading={isLoading}
      excelExportProps={{
        exportData: excelData,
        exportLoading: exportLoading,
        onClickExport,
        fileName: `closet_groups_${params.startDate.split('-').join('')}-${params.endDate
          .split('-')
          .join('')}_${new Date().getTime()}`,
      }}
    />
  );
};

export default GroupList;
